import {Component, effect, inject, signal, viewChild} from '@angular/core';
import {IntersectionObserverDirective} from '../../directives/intersection-observer.directive';
import {WijmoFormatPipe} from '../../pipes/wijmo-format.pipe';
import {WjChartAnalyticsModule, WjFlexChartWaterfall} from '@mescius/wijmo.angular2.chart.analytics';
import {WjChartModule} from '@mescius/wijmo.angular2.chart';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {DashboardTargetsMainComponent} from '../dashboard-targets-main/dashboard-targets-main.component';
import {ColumnGroupDeclaration, Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {CompactSummaryTables} from '../../lib/compact-summary-tables';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {Stdlib} from '../../lib/stdlib';
import {
    PfReportDataHelper,
    PfTargetUwySummaryRow,
    UwStrategyBucketKeyAccount,
    UwStrategyBucketNewBusiness,
    UwStrategyBucketNotRenewable,
    UwStrategyBucketTotal,
    UwStrategyBucketTotalExNewBusiness
} from '../../data/portfolio-segment-report-data';
import {ExcelRowFormat} from '../../lib/wjgrid-customcells';
import {CollectionView, Globalize} from '@mescius/wijmo';
import {AccessControlDirective} from '../../directives/access-control.directive';
import {WjNavModule} from '@mescius/wijmo.angular2.nav';
import {HitTestInfo, IRenderEngine} from '@mescius/wijmo.chart';
import {WaterfallChartDataItem, WjCharts} from '../../lib/wjcharts';

@Component({
    selector: 'app-pf-report-tracking',
    imports: [
        IntersectionObserverDirective,
        WijmoFormatPipe,
        WjChartAnalyticsModule,
        WjChartModule,
        WjGridModule,
        AccessControlDirective,
        WjNavModule
    ],
    templateUrl: './pf-report-tracking.component.html',
    styleUrl: './pf-report-tracking.component.scss'
})
export class PfReportTrackingComponent {

    protected readonly Wijmo = Wijmo;
    protected readonly GeneraliLossRatioWaterfallChartStyle = WjCharts.GeneraliLossRatioWaterfallChartStyle;
    protected readonly GeneraliLossRatioWaterfallChartDeltaLabelsFunction = WjCharts.waterfallChartDeltaLabelsFunction;

    protected readonly pfSegData = inject(PortfolioSegmentDataContext);

    private gridUwySummary = viewChild<WjFlexGrid>('gridUwySummary');
    private gridFinancialYearSummary = viewChild<WjFlexGrid>('gridFinancialYearSummary');
    private gridGnwpByRenewalStatus = viewChild<WjFlexGrid>('gridGnwpByRenewalStatus');
    private gridLlSummary = viewChild<WjFlexGrid>('gridLlSummary');

    protected readonly grandTotalNlrThisUwy = signal(0)
    protected readonly grandTotalPlr = signal(0)

    private uwyWaterfallChart = viewChild<WjFlexChartWaterfall>('uwyWaterfallChart');
    private fyWaterfallChart = viewChild<WjFlexChartWaterfall>('fyWaterfallChart');

    constructor() {
        effect(async () => {
            await this.prepareReportData();
            await this.prepareGrandTotals()
            await this.prepareWaterfallChartData();
            await this.prepareWaterfallChartDataFy();
        });
    }

    private async classificationTiersHighestFirst(): Promise<string[]> {
        await this.pfSegData.waitForResourcesToLoad();
        return this.pfSegData.classificationTierSchema.value()!.tiersNamesHighestFirst();
    }

    // ===========================  Table "Underwriting Year Summary"  ================================
    // =======================================================================================
    async onUwySummaryGridInitialized(grid: WjFlexGrid) {
        const generateTargetInfoColumns = CompactSummaryTables.generateTargetInfoColumns;
        const rdCol = CompactSummaryTables.rdCol;
        const uwy = this.pfSegData.portfolio.value()?.uwyear;

        const columns: ColumnGroupDeclaration[] = [
            { header: ' ', binding: 'bucket', width: 230, height: 100, align: 'center', cssClass: 'bold-text' },
            { header: `${uwy} Expiring Portfolio`, align: 'center', columns: [
                    rdCol('GNWP, m EUR', 'gnwpEur', 'n1,,'),
                    rdCol(`${uwy} NLR`, 'nlr', 'p1')
                ]
            },
            { header: 'Original Portfolio Targets', align: 'center', columns: generateTargetInfoColumns('Org') },
            { header: 'Renewed Portfolio', align: 'center', columns: generateTargetInfoColumns('Renewed') },
            { header: 'Final Remaining', align: 'center', columns: generateTargetInfoColumns('FinalRemaining')  },
            { header: 'New Total Targets', align: 'center', columns: generateTargetInfoColumns('NewTotal') },
        ];

        WjGridUtils.addColumnGroups(grid, columns);
        WjGridUtils.setHeaderRowHeights(grid, 30, 50);
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
    }

    protected onGnwpByRenewalStatusInitialized(grid: WjFlexGrid) {
        function genCols(gnwpBinding: string, nlrBinding: string) {
            const cols = structuredClone(CompactSummaryTables.TargetInfoColumnsGnwpNlr);
            cols[0].binding = gnwpBinding;
            cols[1].binding = nlrBinding;
            return cols;
        }

        const columns: ColumnGroupDeclaration[] = [
            { header: ' ', binding: 'bucket', width: 230, height: 100, align: 'center', cssClass: 'bold-text' },
            { header: 'Renewed Portfolio (Pre)', align: 'center',
                columns: genCols('expGnwpEurRenewed', 'expNlrRenewed')
            },
            { header: 'Lost/Cancelled Portfolio', align: 'center',
                columns: genCols('expGnwpEurLostCancelled', 'expNlrLostCancelled')

            },
            { header: 'Expiring', align: 'center',
                columns: genCols('expGnwpEurRemaining', 'expNlrRemaining')
            },
        ];

        WjGridUtils.addColumnGroups(grid, columns);
        WjGridUtils.setHeaderRowHeights(grid, 30, 50);
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
    }

    async onFinancialYearSummaryGridInitialized(grid: WjFlexGrid) {
        const rdCol = CompactSummaryTables.rdCol;
        const uwy = this.pfSegData.portfolio.value()?.uwyear;

        const columns: ColumnGroupDeclaration[] = [
            { header: ' ', binding: 'bucket', width: 230, align: 'center', cssClass: 'bold-text' },
            { header: `${uwy} Financial Year`, align: 'center', columns: [
                    rdCol('GNEP, m EUR', 'fyGnep', 'n1,,'),
                    rdCol('Earned Rate Change', 'fyEarnedRateChange', 'p1'),
                    rdCol('NLR (FY 2025)', 'fyNlr', 'p1'),
                    rdCol('Plan LR (FY 2025)', 'fyNlr', 'p1'),
                    rdCol('Delta to Plan', 'fyPlrDelta', 'p1')
                ]
            },
        ];

        WjGridUtils.addColumnGroups(grid, columns);
        WjGridUtils.setHeaderRowHeights(grid, 20, 90);
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);

        //grid.formatItem.addHandler(this.accountCellsFormatHandler.bind(this));
    }

    private async prepareReportData() {
        Stdlib.touchSignals(this.pfSegData.wb.uwySummary);
        await this.pfSegData.waitForResourcesToLoad();
        const summary = structuredClone(this.pfSegData.wb.uwySummary());

        // insert a subtotal row before non-renewable
        const nonRenewableIdx = PfReportDataHelper.findSummaryRowIndex(summary, UwStrategyBucketNewBusiness);
        if (nonRenewableIdx > 0) {
            const rowFmt: ExcelRowFormat = { cssClass: 'white-on-white', height: 8, isReadOnly: true };
            const separatorRow: any  = { bucket: '', rowFmt };
            summary.splice(nonRenewableIdx, 0, separatorRow);
        }

        type PfTargetUwySummaryRowCalculations = {
            [key : string]: (row: PfTargetUwySummaryRow) => number | undefined;
        }

        const reportCalculations: PfTargetUwySummaryRowCalculations = {
            gnwpEurOrg: ($) => $.renewalGnwpEur,
            nlrOrg: ($) => $.renewalNlr,
            rateChangeOrg: ($) => $.targetRateChange,
            retentionRateOrg: ($) => $.targetRetentionRate,
        }

        const calculatedSummary = new CollectionView(summary, { calculatedFields: reportCalculations });

        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridUwySummary(), calculatedSummary);
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridFinancialYearSummary(), calculatedSummary);
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridGnwpByRenewalStatus(), calculatedSummary);
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridLlSummary(), summary);
    }

    async onLLSummaryGridInitialized(grid: WjFlexGrid) {
        const rdCol = CompactSummaryTables.rdCol;

        const columns: ColumnGroupDeclaration[] = [
            { header: ' ', binding: 'bucket', width: 230, align: 'center', cssClass: 'bold-text' },
            rdCol('Total Expiring GNWP','gnwpEur', 'n1,,'),
            { header: 'Control Accounts - Expiring Portfolio', align: 'center', columns: [
                    rdCol('#', 'llCount', 'g0'),
                    rdCol('%', 'llPct', 'p1'),
                    rdCol('GNWP', 'llGnwpEur', 'n1,,'),
                    rdCol('Expected Nr. of LL per year', 'llExpectedLlPerYear', 'n3'),
                    rdCol('Intensity (%)', 'llIntensity', 'p2')
                ]
            },
            { header: 'Control Accounts - Renewed Portfolio', align: 'center', columns: [
                    rdCol('GNWP', 'llGnwpEurRenewed', 'n1,,'),
                    rdCol('Expected Nr. of LL per year', 'llExpectedLlPerYearRenewed', 'n3'),
                    rdCol('Intensity (%)', 'llIntensityRenewed', 'p2'),
                    rdCol('Renewal LL Frequency reduction', 'llFrequencyReduction', 'p2'),
                    rdCol('Pruning', 'llPrunedInRenewal', 'p2'),
                ]
            },
        ];

        WjGridUtils.addColumnGroups(grid, columns);
        WjGridUtils.setHeaderRowHeights(grid, 20, 90);
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
    }

    private async prepareGrandTotals() {
        await this.pfSegData.waitForResourcesToLoad();
        this.grandTotalNlrThisUwy.set(this.pfSegData.wb.configMatrixValue('nlr') ?? 0);
        this.grandTotalPlr.set(this.pfSegData.wb.configMatrixValue('plr') ?? 0);
    }

    protected async prepareWaterfallChartData() {
        const chart = this.uwyWaterfallChart();
        const summary = structuredClone(this.pfSegData.wb.uwySummary());

        const totalExNbRow = summary.find(row => row.bucket === UwStrategyBucketTotalExNewBusiness);
        const totalRow = summary.find(row => row.bucket === UwStrategyBucketTotal);

        if (totalRow && totalExNbRow && chart) {
            const uwy = this.pfSegData.wb.uwy;
            const nlrUwy = nv(totalExNbRow.nlr);
            const chartData: WaterfallChartDataItem[] = [ { name: `${uwy} NLR`, value: nlrUwy }];

            function nv(v: number | undefined) { return v ?? 0; }

            let gnwp = nv(totalExNbRow.gnwpEur);
            let loss = gnwp * nlrUwy;

            const buckets = await this.classificationTiersHighestFirst();
            buckets.push(UwStrategyBucketKeyAccount, UwStrategyBucketNotRenewable);
            buckets.forEach(buckets => {
                const row = PfReportDataHelper.findSummaryRow(summary, buckets)!;
                gnwp += -nv(row.gnwpEur) + nv(row.renewalGnwpEur);
                loss -= nv(row.gnwpEur) * nv(row.nlr) - nv(row.gnwpEurNewTotal) * nv(row.nlrNewTotal);
                const lr = loss / gnwp;

                chartData.push({ name: buckets, value: lr });
            });

            const totals = [{ position: chartData.length, label: UwStrategyBucketTotalExNewBusiness }];

            // 'new business' entry - is strangley based on total ...
            chartData.push({
                name: UwStrategyBucketNewBusiness,
                value: nv(totalRow.renewalClaimsEur) / nv(totalRow.renewalGnwpEur)
            });
            totals.push({ position: chartData.length, label: 'Target NLR' });

            chartData.push({
                name: 'Delta to Plan',
                value: nv(this.pfSegData.wb.configMatrixValue('plr'))
            });
            totals.push({ position: chartData.length, label: 'Plan LR' });

            WjCharts.fillWaterfallChartDeltaLabels(chartData);

            chart.chart.itemsSource = chartData;
            chart.intermediateTotalPositions = totals.map(it => it.position);
            chart.intermediateTotalLabels = totals.map(it => it.label);
            chart.showIntermediateTotal = true;
            chart.totalLabel = '**Total';
            chart.tooltipContent = (hti: HitTestInfo) => `${hti.x}: ${Globalize.format(hti.y, 'p1')}`

        }
    }

    protected async prepareWaterfallChartDataFy() {
        const chart = this.fyWaterfallChart();
        const summary = structuredClone(this.pfSegData.wb.uwySummary());

        const totalExNbRow = summary.find(row => row.bucket === UwStrategyBucketTotalExNewBusiness);
        const totalRow = summary.find(row => row.bucket === UwStrategyBucketTotal);

        if (totalRow && totalExNbRow && chart) {
            const uwy = this.pfSegData.wb.uwy;
            const nlrFy = this.grandTotalNlrThisUwy();

            const chartData: WaterfallChartDataItem[] = [ { name: `${uwy} NLR`, value: nlrFy }];

            function nv(v: number | undefined) { return v ?? 0; }

            let gnwp = nv(totalExNbRow.gnwpEur);
            let loss = gnwp * nlrFy;

            const buckets = await this.classificationTiersHighestFirst();
            buckets.push(UwStrategyBucketKeyAccount, UwStrategyBucketNotRenewable);
            buckets.forEach(buckets => {
                const row = PfReportDataHelper.findSummaryRow(summary, buckets)!;
                gnwp -= nv(row.fyGnwpEarnedInUwy);
                loss -= nv(row.fyGnwpEarnedInUwy) * nv(row.nlr); // - nv(row.fyNlr) * nv(row.fyGnep);
                const lr = loss / gnwp;

                chartData.push({ name: buckets, value: lr });
            });

            const totals = [{ position: chartData.length, label: UwStrategyBucketTotalExNewBusiness }];

            // 'new business' entry - is strangley based on total ...
            chartData.push({
                name: UwStrategyBucketNewBusiness,
                value: nv(totalRow.fyNlr)
            });
            totals.push({ position: chartData.length, label: 'Target NLR' });

            chartData.push({
                name: 'Delta to Plan',
                value: nv(this.pfSegData.wb.configMatrixValue('plr'))
            });
            totals.push({ position: chartData.length, label: 'Plan LR' });

            WjCharts.fillWaterfallChartDeltaLabels(chartData);

            chart.chart.itemsSource = chartData;
            chart.intermediateTotalPositions = totals.map(it => it.position);
            chart.intermediateTotalLabels = totals.map(it => it.label);
            chart.showIntermediateTotal = true;
            chart.totalLabel = '**Total';
            chart.tooltipContent = (hti: HitTestInfo) => `${hti.x}: ${Globalize.format(hti.y, 'p1')}`
        }
    }

    protected WaterfallChartPaletteItemFormatter(engine: IRenderEngine, ht: HitTestInfo, defaultRenderer: Function) {
        const index = ht.pointIndex;
        const item = ht.item;
        if (index === 0) {
            engine.fill = WjCharts.GeneraliWaterfallChartStyle.intermediateTotal.fill;
            engine.stroke = WjCharts.GeneraliWaterfallChartStyle.intermediateTotal.stroke;
        } if (/delta/i.test(item.name)) {
            engine.fill = WjCharts.GeneraliWaterfallChartStyle.total.fill;
            engine.stroke = WjCharts.GeneraliWaterfallChartStyle.total.stroke;
        }

        defaultRenderer();
    }
}
